import { AxiosError } from 'axios';

export class Unauthenticated extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

export class Unauthorized extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

const NETWORK_ERROR_MESSAGE =
  'Network error. Please check your internet connection and refresh the page';
// The backend also answers 401 for some authorization failures, so a lost session is only
// one of the possible causes.
const ACCESS_DENIED_MESSAGE =
  'Access denied. Your session may have expired, reload the page to sign in again.';

interface WrappedResponse {
  status?: number;
  data?: { message?: string };
}

// #FIXME: AxiosError is not a good type here. It leaks implementation details from the core.
export function getApiErrorMessage(
  error: AxiosError | unknown | undefined,
  defaultErrorMessage: string,
) {
  if (error instanceof AxiosError) {
    if (error.code === AxiosError.ERR_NETWORK || error.code === AxiosError.ETIMEDOUT) {
      return { message: NETWORK_ERROR_MESSAGE };
    }

    if (error.response?.status === 401) {
      return { message: ACCESS_DENIED_MESSAGE };
    }

    if (error.response?.data?.message) {
      return {
        message: 'API error',
        cause: error.response.data.message,
      };
    }

    const message = error.response?.data?.message || error.message;
    if (error.status === 500) {
      return {
        message: `API error\n${message}\nIf something like this happens again, please contact support`,
      };
    }

    return {
      message: message ? `API error:\n${message}` : defaultErrorMessage,
    };
  }

  // The wizards rethrow failures wrapped in their own Error subclasses, which copy the
  // original `response` over; a network failure leaves it undefined.
  const response =
    error instanceof Object && 'response' in error
      ? (error as { response?: WrappedResponse }).response
      : undefined;

  if (response?.status === 401) {
    return { message: ACCESS_DENIED_MESSAGE };
  }

  if (response?.data?.message) {
    return {
      message: 'API error',
      cause: response.data.message,
    };
  }

  return { message: defaultErrorMessage };
}
